import type { RequestOptions } from '@@/plugin-request/request';
import { request as umiRequest } from '@umijs/max';
import { BizAppConfig } from 'config/application.manifest';
import { unwrapOwnBffData } from '@/services/bff/envelope';
import {
  assertRequestContextStillValid,
  attachRequestContext,
  captureOwnBackendRequestContext,
  resolveRequestContext,
} from '@/services/requestContext';

const normalizeBaseURL = (baseURL?: string) => {
  if (!baseURL) {
    return '';
  }

  const trimmed = baseURL.trim().replace(/@+$/u, '');
  return trimmed.endsWith('/') ? trimmed.slice(0, -1) : trimmed;
};

const sendRequest = async <T = unknown>(
  url: string,
  scopedBaseURL: string,
  options: RequestOptions | undefined,
  requirePark: boolean,
) => {
  const snapshot = resolveRequestContext(options, { requirePark });
  if (requirePark && !snapshot.parkUuid) {
    throw new Error('缺少 ParkContext');
  }

  try {
    const response = await (
      umiRequest as unknown as <R = unknown>(
        requestUrl: string,
        requestOptions?: RequestOptions,
      ) => Promise<R>
    )<unknown>(url, {
      baseURL: scopedBaseURL,
      ...attachRequestContext(options, snapshot),
      headers: {
        ...(options?.headers || {}),
      },
    });
    assertRequestContextStillValid(snapshot);
    return unwrapOwnBffData<T>(response);
  } catch (error) {
    assertRequestContextStillValid(snapshot);
    throw error;
  }
};

export const createRequest = (baseURL?: string) => {
  const scopedBaseURL = normalizeBaseURL(baseURL ?? BizAppConfig.appApiUrl);
  return <T = unknown>(url: string, options?: RequestOptions) => {
    const snapshot = captureOwnBackendRequestContext({ requirePark: false });
    return sendRequest<T>(
      url,
      scopedBaseURL,
      attachRequestContext(options, snapshot),
      false,
    );
  };
};

export const createParkRequest = (baseURL?: string) => {
  const scopedBaseURL = normalizeBaseURL(baseURL ?? BizAppConfig.appApiUrl);
  return <T = unknown>(url: string, options?: RequestOptions) => {
    const snapshot = captureOwnBackendRequestContext({ requirePark: true });
    if (!snapshot.parkUuid) {
      return Promise.reject(new Error('缺少 ParkContext'));
    }
    return sendRequest<T>(
      url,
      scopedBaseURL,
      attachRequestContext(
        {
          ...options,
          headers: {
            'x-park-uuid': snapshot.parkUuid,
            ...(options?.headers || {}),
          },
        },
        snapshot,
      ),
      true,
    );
  };
};

export const createAppRequest = () => createRequest(BizAppConfig.appApiUrl);
export const createTenantManagementRequest = () =>
  createRequest(BizAppConfig.appTenantManagementUrl);
export const createOpenPlatformRequest = (baseURL?: string) =>
  createRequest(baseURL);
